/**
 * CardV2 - 新カードコンポーネント
 *
 * 設計書: src/ルール/ui/card-design-system.md
 *
 * 特徴:
 * - 750x975基準でレイアウトし、transform: scale()で縮小表示
 * - displayModeで表示要素を切り替え
 * - 高解像度対応（detail基準で1500x1950も可能）
 */

import React, { useRef, useCallback, useState } from 'react';
import { ATTRIBUTE_COLORS, TYPE_ICONS, CARD_BACK } from '../../utils/constants';
import { isSetsunaMagic } from '../../engine/keywordAbilities';
import AttributeIcon from '../AttributeIcon';
import { useCardIllustration } from '../../hooks/useCardIllustration';
import type { CardData } from '../../types';
import {
  CARD_BASE_SIZE,
  CARD_DISPLAY_CONFIG,
  CARD_LAYOUT,
  TEXT_STYLES,
  COST_COLORS,
  SKILL_ICON_COLORS,
  TYPE_ICON_COLORS,
  HAND_INTERACTION,
  DEX_LIST_BOTTOM_INFO_HEIGHT,
  DEX_LIST_STYLES,
  getCardScale,
  type CardDisplayMode,
} from './cardConstants';

// ========================================
// 型定義
// ========================================

interface CardV2Props {
  card: CardData | null;
  /** 表示モード */
  displayMode?: CardDisplayMode;
  /** 表示幅（px） - これに基づいてスケール計算 */
  displayWidth?: number;
  /** 選択状態 */
  selected?: boolean;
  /** ホバー状態（扇形表示用） */
  isHovered?: boolean;
  /** 無効状態 */
  disabled?: boolean;
  /** 裏向き */
  faceDown?: boolean;
  /** コスト修正値 */
  modifiedCost?: number;
  /** コスト修正の理由 */
  costModifierSource?: string;
  /** 回転角度（度） - 扇形表示用 */
  rotation?: number;
  /** Y方向オフセット（px） - 扇形表示用 */
  offsetY?: number;
  /** z-index - 扇形表示用 */
  zIndex?: number;
  /** クリックハンドラ */
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** 長押しハンドラ（モバイル用） */
  onLongPress?: (card: CardData) => void;
  /** マウスエンター */
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** マウスリーブ */
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** モバイルフラグ */
  isMobile?: boolean;

  // === 図鑑一覧用（dex-list モード） ===

  /** 所持状態（スタイル制御用） */
  isOwned?: boolean;
  /** 所持バッジテキスト（例: "3種"） */
  ownedBadgeText?: string;
  /** 未所持マーク表示（中央に「?」） */
  showNotOwnedMark?: boolean;
  /** ウィッシュリストハート表示 */
  showWishlistHeart?: boolean;
  /** ウィッシュリスト状態 */
  inWishlist?: boolean;
  /** ウィッシュリストトグルハンドラ */
  onWishlistToggle?: (e: React.MouseEvent) => void;
  /** 下部にカード名を表示 */
  showBottomName?: boolean;
  /** 下部にコストを表示 */
  showBottomCost?: boolean;
}

// ========================================
// メインコンポーネント
// ========================================

const CardV2: React.FC<CardV2Props> = ({
  card,
  displayMode = 'hand',
  displayWidth = 100,
  selected = false,
  isHovered = false,
  disabled = false,
  faceDown = false,
  modifiedCost,
  costModifierSource,
  rotation = 0,
  offsetY = 0,
  zIndex,
  onClick,
  onLongPress,
  onMouseEnter,
  onMouseLeave,
  isMobile = false,
  // 図鑑一覧用
  isOwned = true,
  ownedBadgeText,
  showNotOwnedMark = false,
  showWishlistHeart = false,
  inWishlist = false,
  onWishlistToggle,
  showBottomName = false,
  showBottomCost = false,
}) => {
  // 図鑑一覧用の内部ホバー状態
  const [isDexListHovered, setIsDexListHovered] = useState(false);
  const isDexListMode = displayMode === 'dex-list';
  const isCollectionMode = displayMode === 'collection';
  // dex-listとcollectionモードは同様のレイアウト処理
  const isListMode = isDexListMode || isCollectionMode;
  // 長押し検出
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredAt = useRef<number>(0);

  const handleTouchStart = useCallback(() => {
    if (!isMobile || !onLongPress || faceDown || !card) return;
    longPressTriggeredAt.current = 0;
    longPressTimer.current = setTimeout(() => {
      longPressTriggeredAt.current = Date.now();
      onLongPress(card);
    }, 500);
  }, [isMobile, onLongPress, card, faceDown]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (longPressTriggeredAt.current > 0) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (longPressTriggeredAt.current > 0 && Date.now() - longPressTriggeredAt.current < 300) {
      longPressTriggeredAt.current = 0;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    longPressTriggeredAt.current = 0;
    if (!disabled && onClick) {
      onClick(e);
    }
  }, [disabled, onClick]);

  // イラスト検出
  const { hasIllustration, illustrationPath, frameImage } = useCardIllustration(
    card?.id,
    card?.attribute || 'なし'
  );

  // 表示設定
  const config = CARD_DISPLAY_CONFIG[displayMode];
  const baseSize = CARD_BASE_SIZE[config.baseSize];
  const scale = getCardScale(displayWidth, config.baseSize);
  const cardImageHeight = Math.round(baseSize.height * scale);
  // dex-listモードでは下部テキストエリア分を追加
  const bottomInfoHeight = isDexListMode && (showBottomName || showBottomCost) ? DEX_LIST_BOTTOM_INFO_HEIGHT : 0;
  const displayHeight = cardImageHeight + bottomInfoHeight;

  // カード情報
  const colors = card ? (ATTRIBUTE_COLORS[card.attribute] || ATTRIBUTE_COLORS['なし']) : ATTRIBUTE_COLORS['なし'];
  const isMonster = card?.type === 'monster';

  // 図鑑一覧用スタイル
  const dexListOwnedStyle = isDexListMode
    ? (isOwned ? DEX_LIST_STYLES.cardOwned : DEX_LIST_STYLES.cardNotOwned)
    : {};

  // Null check
  if (!card) return null;

  // ========================================
  // 裏向き表示
  // ========================================
  if (faceDown) {
    return (
      <div
        style={{
          width: displayWidth,
          height: displayHeight,
          overflow: 'hidden',
          borderRadius: 8 * scale,
        }}
      >
        <div
          style={{
            width: baseSize.width,
            height: baseSize.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            // 注意: background ショートハンドを使用して個別プロパティとの競合を防ぐ
            background: `url(${CARD_BACK}) center/cover no-repeat`,
            borderRadius: 8,
          }}
        />
      </div>
    );
  }

  // ========================================
  // コスト計算
  // ========================================
  const actualCost = modifiedCost !== undefined ? modifiedCost : card.cost;
  const isCostReduced = modifiedCost !== undefined && modifiedCost < card.cost;
  const isCostIncreased = modifiedCost !== undefined && modifiedCost > card.cost;
  const costColorKey = isCostReduced ? 'reduced' : isCostIncreased ? 'increased' : 'normal';

  // ========================================
  // 扇形表示のY調整計算
  // ========================================
  const liftY = selected
    ? HAND_INTERACTION.selectedLiftY
    : isHovered
      ? HAND_INTERACTION.hoverLiftY
      : 0;
  const totalOffsetY = offsetY + liftY;
  const effectiveZIndex = selected
    ? HAND_INTERACTION.selectedZIndex
    : zIndex;

  // ========================================
  // レンダリング
  // ========================================

  // dex-listモード用のホバーハンドラ
  const handleDexListMouseEnter = isDexListMode
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        setIsDexListHovered(true);
        onMouseEnter?.(e);
      }
    : onMouseEnter;

  const handleDexListMouseLeave = isDexListMode
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        setIsDexListHovered(false);
        onMouseLeave?.(e);
      }
    : onMouseLeave;

  // dex-listモード用のホバースタイル
  const dexListHoverStyle = isDexListMode && isDexListHovered ? DEX_LIST_STYLES.cardHover : {};

  return (
    <div
      style={{
        // dex-list/collectionモードではrelative（グリッド配置用）
        position: isListMode ? 'relative' : 'absolute',
        width: displayWidth,
        height: displayHeight,
        overflow: 'visible',
        borderRadius: isListMode ? 10 : 8 * scale,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // dex-list/collectionモードでは所持状態でfilterを制御（collectionは常にownd）
        filter: isDexListMode
          ? (isOwned ? 'none' : 'grayscale(0.5)')
          : isCollectionMode
            ? 'none'
            : (disabled ? 'grayscale(0.8) brightness(0.6)' : 'none'),
        opacity: isDexListMode ? (isOwned ? 1 : 0.5) : 1,
        transition: `transform ${HAND_INTERACTION.transitionDuration} ${HAND_INTERACTION.transitionEasing}, box-shadow ${HAND_INTERACTION.transitionDuration} ${HAND_INTERACTION.transitionEasing}`,
        transform: isDexListMode
          ? (isDexListHovered ? 'translateY(-2px)' : 'none')
          : isCollectionMode
            ? 'none'
            : `translateY(${totalOffsetY}px) rotate(${rotation}deg)`,
        transformOrigin: isListMode ? 'center center' : 'center bottom',
        zIndex: effectiveZIndex,
        boxShadow: isDexListMode
          ? (isDexListHovered ? '0 4px 12px rgba(107, 76, 230, 0.3)' : '0 2px 8px rgba(0,0,0,0.3)')
          : isCollectionMode
            ? 'none'  // collectionモードではRarityEffectLayerが影を担当
            : (selected
                ? `0 0 20px ${colors.glow}, 0 0 40px rgba(255,215,0,0.5)`
                : isHovered
                  ? `0 8px 25px rgba(0,0,0,0.6)`
                  : `0 4px 15px rgba(0,0,0,0.4)`),
        // dex-listモード用の背景・ボーダー（collectionモードはRarityEffectLayerが担当）
        // 注意: backgroundショートハンドとbackgroundImageの混在を避けるため、backgroundImageを使用
        ...(isDexListMode ? { backgroundImage: 'linear-gradient(135deg, #1a1a3a 0%, #2a2a4a 100%)' } : {}),
        padding: isDexListMode ? 10 : undefined,
        borderStyle: isCollectionMode ? 'none' : 'solid',
        borderWidth: isDexListMode ? 2 : 0,
        borderColor: isDexListMode
          ? (isOwned ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 255, 255, 0.1)')
          : 'transparent',
      }}
      onClick={handleClick}
      onMouseEnter={handleDexListMouseEnter}
      onMouseLeave={handleDexListMouseLeave}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
      onTouchMove={isMobile ? handleTouchEnd : undefined}
    >
      {/* カード画像コンテナ（視覚的なサイズに合わせてクリップ） */}
      <div
        style={{
          width: displayWidth,
          height: cardImageHeight,
          overflow: 'hidden',
          borderRadius: isListMode ? 8 : 8 * scale,
        }}
      >
        {/* 内部コンテナ（750x975基準） */}
        <div
          style={{
            width: baseSize.width,
            height: baseSize.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            position: 'relative',
            // 背景: イラストがあればイラスト、なければsolid枠
            // 注意: background ショートハンドを使用して個別プロパティとの競合を防ぐ
            background: `url(${hasIllustration ? illustrationPath : frameImage}) center/cover no-repeat`,
            borderRadius: 8,
            border: selected ? '4px solid #ffd700' : '2px solid rgba(255,255,255,0.3)',
          }}
        >
        {/* 透過カード枠（イラストがある場合のみ） */}
        {hasIllustration && (
          <img
            src={frameImage}
            alt=""
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
              zIndex: 1,
            }}
            draggable={false}
          />
        )}

        {/* UI要素レイヤー */}
        <div style={{ position: 'relative', zIndex: 2, width: '100%', height: '100%' }}>

          {/* コスト */}
          {config.showCost && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.cost.y,
                left: CARD_LAYOUT.cost.x,
                width: CARD_LAYOUT.cost.size,
                height: CARD_LAYOUT.cost.size,
                borderRadius: '50%',
                background: COST_COLORS[costColorKey].background,
                boxShadow: COST_COLORS[costColorKey].shadow,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: CARD_LAYOUT.cost.fontSize,
                fontWeight: 'bold',
                color: '#fff',
              }}
              title={costModifierSource || `コスト: ${actualCost}`}
            >
              {actualCost}
            </div>
          )}

          {/* 刹那詠唱マーカー */}
          {config.showCost && isSetsunaMagic(card) && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.cost.y,
                left: CARD_LAYOUT.cost.x + CARD_LAYOUT.cost.size + 10,
                width: 40,
                height: 40,
                borderRadius: 6,
                background: 'linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                boxShadow: '0 0 12px rgba(0, 188, 212, 0.9)',
              }}
              title="【刹那詠唱】相手ターンでも発動可能（コスト+1）"
            >
              ⚡
            </div>
          )}

          {/* カード名 */}
          {config.showName && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.name.y,
                left: CARD_LAYOUT.cost.x + CARD_LAYOUT.cost.size + 20,
                right: CARD_LAYOUT.attributeIcon.xFromRight + CARD_LAYOUT.attributeIcon.size + 20,
                fontSize: CARD_LAYOUT.name.fontSize,
                fontWeight: 'bold',
                color: '#fff',
                ...TEXT_STYLES.outline,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {card.name}
            </div>
          )}

          {/* カテゴリ */}
          {config.showCategory && card.category && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.category.y,
                left: CARD_LAYOUT.cost.x + CARD_LAYOUT.cost.size + 20,
                right: CARD_LAYOUT.attributeIcon.xFromRight + CARD_LAYOUT.attributeIcon.size + 20,
                fontSize: CARD_LAYOUT.category.fontSize,
                color: 'rgba(255,255,255,0.9)',
                ...TEXT_STYLES.outline,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {card.category}
            </div>
          )}

          {/* 属性アイコン */}
          {config.showAttributeIcon && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.attributeIcon.y,
                right: CARD_LAYOUT.attributeIcon.xFromRight,
              }}
            >
              <AttributeIcon
                attribute={card.attribute}
                size={CARD_LAYOUT.attributeIcon.size}
              />
            </div>
          )}

          {/* タイプアイコン（コストの下） */}
          {config.showTypeIcon && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.typeIcon.y,
                left: CARD_LAYOUT.typeIcon.x,
                width: CARD_LAYOUT.typeIcon.size,
                height: CARD_LAYOUT.typeIcon.size,
                fontSize: CARD_LAYOUT.typeIcon.fontSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: TYPE_ICON_COLORS[card.type]?.background || 'rgba(0, 0, 0, 0.5)',
                boxShadow: TYPE_ICON_COLORS[card.type]?.shadow || '0 2px 8px rgba(0,0,0,0.4)',
                borderRadius: '50%',
                border: '3px solid rgba(255, 255, 255, 0.4)',
              }}
              title={card.type === 'monster' ? 'モンスター' : card.type === 'magic' ? '魔法' : card.type === 'field' ? 'フィールド' : 'フェイズ'}
            >
              {TYPE_ICONS[card.type]}
            </div>
          )}

          {/* 技アイコン（モンスターのみ） */}
          {config.showSkillIcons && isMonster && (card.basicSkill || card.advancedSkill) && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.skillIcons.y,
                left: CARD_LAYOUT.skillIcons.x,
                display: 'flex',
                gap: CARD_LAYOUT.skillIcons.gap,
              }}
            >
              {card.basicSkill && (
                <div
                  style={{
                    width: CARD_LAYOUT.skillIcons.size,
                    height: CARD_LAYOUT.skillIcons.size,
                    borderRadius: '50%',
                    background: SKILL_ICON_COLORS.basic.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: CARD_LAYOUT.skillIcons.fontSize,
                    fontWeight: 'bold',
                    color: '#fff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                  }}
                  title={`基本技: ${card.basicSkill.text}`}
                >
                  1
                </div>
              )}
              {card.advancedSkill && (
                <div
                  style={{
                    width: CARD_LAYOUT.skillIcons.size,
                    height: CARD_LAYOUT.skillIcons.size,
                    borderRadius: '50%',
                    background: SKILL_ICON_COLORS.advanced.background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: CARD_LAYOUT.skillIcons.fontSize,
                    fontWeight: 'bold',
                    color: '#fff',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
                  }}
                  title={`上級技: ${card.advancedSkill.text}`}
                >
                  2
                </div>
              )}
            </div>
          )}

          {/* 効果テキスト */}
          {config.showEffect && card.effect && (
            <div
              style={{
                position: 'absolute',
                top: CARD_LAYOUT.effect.y,
                left: CARD_LAYOUT.effect.x,
                width: CARD_LAYOUT.effect.width,
                height: CARD_LAYOUT.effect.height,
                fontSize: CARD_LAYOUT.effect.fontSize,
                lineHeight: CARD_LAYOUT.effect.lineHeight,
                color: '#111',
                ...TEXT_STYLES.outlineWhite,
                overflow: 'hidden',
                wordBreak: 'break-all',
              }}
            >
              {card.effect}
            </div>
          )}

          {/* ATK */}
          {config.showStats && isMonster && (
            <div
              style={{
                position: 'absolute',
                bottom: baseSize.height - CARD_LAYOUT.atk.y - 60,
                left: CARD_LAYOUT.atk.x,
                fontSize: CARD_LAYOUT.atk.fontSize,
                fontWeight: 900,
                color: '#fff',
                ...TEXT_STYLES.outlineThick,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 40 }}>⚔️</span>
              {(card as any).currentAttack || card.attack}
            </div>
          )}

          {/* HP */}
          {config.showStats && isMonster && (
            <div
              style={{
                position: 'absolute',
                bottom: baseSize.height - CARD_LAYOUT.hp.y - 60,
                right: CARD_LAYOUT.hp.xFromRight,
                fontSize: CARD_LAYOUT.hp.fontSize,
                fontWeight: 900,
                color: '#6bff6b',
                ...TEXT_STYLES.outlineThick,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 40 }}>❤️</span>
              {(card as any).currentHp || card.hp}
            </div>
          )}

          {/* ========================================
              図鑑一覧用オーバーレイ要素（750x975基準内）
              ======================================== */}

          {/* ウィッシュリストハート */}
          {isDexListMode && showWishlistHeart && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWishlistToggle?.(e);
              }}
              style={{
                position: 'absolute',
                top: 40,
                right: ownedBadgeText ? 280 : 40,
                background: 'rgba(0, 0, 0, 0.6)',
                border: 'none',
                borderRadius: '50%',
                width: 160,
                height: 160,
                fontSize: 90,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'transform 0.15s ease',
              }}
              title={inWishlist ? 'ウィッシュリストから削除' : 'ウィッシュリストに追加'}
            >
              {inWishlist ? '❤️' : '🩶'}
            </button>
          )}

          {/* 所持バッジ */}
          {isDexListMode && ownedBadgeText && (
            <div
              style={{
                position: 'absolute',
                top: 40,
                right: 40,
                background: '#4caf50',
                color: '#fff',
                padding: '15px 40px',
                borderRadius: 20,
                fontSize: 80,
                fontWeight: 'bold',
                zIndex: 5,
              }}
            >
              {ownedBadgeText}
            </div>
          )}

          {/* 未所持マーク */}
          {isDexListMode && showNotOwnedMark && !isOwned && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'rgba(160, 160, 160, 0.8)',
                fontSize: 120,
                fontWeight: 'bold',
                zIndex: 5,
                textShadow: '0 4px 16px rgba(0,0,0,0.5)',
              }}
            >
              ?
            </div>
          )}
        </div>
      </div>
      </div>

      {/* ========================================
          図鑑一覧用 下部テキスト
          ======================================== */}
      {isDexListMode && (showBottomName || showBottomCost) && (
        <div style={DEX_LIST_STYLES.bottomInfo}>
          {showBottomName && (
            <div style={DEX_LIST_STYLES.bottomName}>
              {card.name}
            </div>
          )}
          {showBottomCost && (
            <div style={DEX_LIST_STYLES.bottomCost}>
              コスト: {card.cost}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CardV2;
